// Datos de Análisis. TRES endpoints de TRES lambdas distintos, y no se pueden fusionar:
//
//   · `GET /backoffice/sales/analytics` (wizard, 29a) — los cinco tableros de venta
//   · `GET /backoffice/inventory/rotation` (catalog, 29b) — la rotación por tienda
//   · `GET /backoffice/sellers/kpis` (wizard) — el nombre de TIENDA de cada vendedor
//
// El inventario no vive en el wizard, así que pedirle los dos a un solo lambda habría significado
// que uno le pegara al otro para pintar media pantalla. Se piden en paralelo y se cruzan por id.
//
// Ninguno toma rango: el de ventas trae los TRES del selector en una sola respuesta, y la rotación
// es una foto del piso de hoy. Cambiar de pill NO vuelve a pegarle a la API.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '@/api/services/retail-wizard/backoffice';
import { inventoryLensApi } from '@/api/services/retail-catalog/backoffice';
import { sellerKpisApi } from '@/api/services/retail-org/backoffice';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { buildStagnantInventory } from '../lib/stagnantInventory';

const KEY = ['retail-admin', 'analisis'] as const;

export function useAnalisis() {
    return useQuery({
        queryKey: [...KEY, 'sales'],
        queryFn: () => analyticsApi.getAnalytics(),
        staleTime: 5 * 60_000,
    });
}

/**
 * La rotación va en su PROPIO query, no en el mismo: si el lente de inventario falla, los cinco
 * tableros de venta tienen que pintarse igual. Una sola promesa combinada tumbaría la pantalla
 * entera por el bloque que menos se mira.
 */
export function useAnalisisRotation() {
    return useQuery({
        queryKey: [...KEY, 'rotation'],
        queryFn: () => inventoryLensApi.getRotation(),
        staleTime: 5 * 60_000,
    });
}

/**
 * El padrón de vendedores, SOLO para saber en qué tienda está cada uno.
 *
 * El mock pone la tienda bajo el nombre en «top vs bottom» y en «mes contra mes», y el 29a no la
 * manda: su `sellerRanking` trae `sellerRetailUserId` y `sellerName`, nada más. En vez de dejar el
 * renglón cojo o —peor— inventar la tienda, se cruza con la lista que SÍ la tiene.
 *
 * `retry: false`: si falla, la pantalla pierde una línea de contexto, no un tablero. No vale la
 * pena retrasar el render tres veces por eso.
 */
export function useAnalisisSellerStores() {
    return useQuery({
        queryKey: [...KEY, 'seller-stores'],
        queryFn: () => sellerKpisApi.listSellers(),
        staleTime: 5 * 60_000,
        retry: false,
    });
}

/** Inventario estancado y capital atado del periodo, desde la lente del catálogo (modo lista). */
export function useAnalisisStagnantInventory(period: InventoryLensPeriod) {
    const query = useQuery({
        queryKey: [...KEY, 'stagnant', period],
        queryFn: () => inventoryLensApi.getList({ period }),
        staleTime: 5 * 60_000,
    });
    const data = useMemo(
        () => (query.data ? buildStagnantInventory(query.data.items) : undefined),
        [query.data],
    );
    return { data, error: query.error, isPending: query.isPending, refetch: query.refetch };
}
